import * as React from "react";
import { cva } from "class-variance-authority";

import { cn } from "utils/cn";

const avatarVariants = cva(
  "relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-medium leading-none",
  {
    variants: {
      size: {
        sm: "h-6 w-6 text-xxs",
        md: "h-8 w-8 text-xs",
        lg: "h-10 w-10 text-sm",
        xl: "h-16 w-16 text-xl",
      },
    },
    defaultVariants: { size: "md" },
  }
);

// Initials backgrounds. A name always hashes to the same tone, so a person keeps their color.
const TONES = [
  "bg-primary-subtle text-primary-text",
  "bg-success-subtle text-success-text",
  "bg-info-subtle text-info-text",
  "bg-warning-subtle text-warning-text",
];

const toneFor = (name: string): string => {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }
  return TONES[Math.abs(hash) % TONES.length];
};

/** "Aman Malik" → "AM", "aman" → "A". Array.from keeps emoji and accented letters whole. */
export const getInitials = (name: string): string => {
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => Array.from(word)[0] || "")
    .join("")
    .toUpperCase();
  return initials || "?";
};

export interface AvatarProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  /** Person or client name; used for the alt text and the initials fallback. */
  name: string;
  src?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

/** Round photo with an initials fallback when there is no image or it fails to load. */
export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ name, src, size = "md", className, ...props }, ref) => {
    const [failed, setFailed] = React.useState(false);

    React.useEffect(() => {
      setFailed(false);
    }, [src]);

    const showImage = Boolean(src) && !failed;

    return (
      <span
        ref={ref}
        data-ui="avatar"
        role="img"
        aria-label={name}
        className={cn(avatarVariants({ size }), !showImage && toneFor(name), className)}
        {...props}
      >
        {showImage ? (
          <img
            src={src}
            alt=""
            className="h-full w-full bg-surface-muted object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          <span aria-hidden="true">{getInitials(name)}</span>
        )}
      </span>
    );
  }
);
Avatar.displayName = "Avatar";
